import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import SearchPicker from "@/components/SearchPicker";
import { PurchaseProductPicker } from "@/components/PurchaseProductPicker";
import { Loader2, Truck, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const genId = () => Math.random().toString(36).slice(2, 10);

// Satır bazlı para birimi — mevcut CurrencyCode/TRY-USD-EUR-GBP yapısı REUSE
const CUR_LIST = ["TRY", "USD", "EUR", "GBP"];
const CUR_SYMBOL = { TRY: "₺", USD: "$", EUR: "€", GBP: "£" };

const makeEmptyLine = (initialMaterial = null) => ({
  id: genId(),
  material_type: initialMaterial?.type || "product",
  material_id: initialMaterial?.id || "",
  quantity: "",
  unit: initialMaterial?.type === "fabric" ? "kg" : "adet",
  unit_price: "",
  currency: "TRY",
});

/**
 * Reusable multi-line "New Purchase" dialog.
 *
 * Header: Common fields (supplier + invoice + warehouse + dates + notes)
 * Body: Array of purchase lines. Each line uses SearchPicker for material
 * (letter-by-letter search, shows `Name - Code`). Multiple fabrics/accessories
 * can be added in one save. On save the frontend loops POST /purchases per
 * line so backend contract stays the same.
 */
export default function NewPurchaseDialog({
  open,
  onOpenChange,
  supplierId = "",
  supplierName = "",
  suggestedSupplierId = "", // önerilir ama değiştirilebilir
  initialMaterial = null, // { type: 'fabric'|'accessory', id }
  initialLines = null, // Stok Yönetimi > Sipariş Ver: beden başına hazır ürün satırları
  onSuccess,
}) {
  const { can } = useAuth();
  const today = new Date().toISOString().slice(0, 10);

  // Common header fields
  const [supplier, setSupplier] = useState(supplierId);
  const [purchaseDate, setPurchaseDate] = useState(today);
  const [warehouseEntryDate, setWarehouseEntryDate] = useState("");
  const [invoiceNo, setInvoiceNo] = useState("");
  const [deliveryNoteNo, setDeliveryNoteNo] = useState("");
  const [lotNo, setLotNo] = useState("");
  const [supplierLotNo, setSupplierLotNo] = useState("");
  const [warehouseLocation, setWarehouseLocation] = useState("");
  const [notes, setNotes] = useState("");

  // Multi-line rows
  const [lines, setLines] = useState([makeEmptyLine(initialMaterial)]);

  // Reference data
  const [fabrics, setFabrics] = useState([]);
  const [accessories, setAccessories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [warehouses, setWarehouses] = useState([]);

  const [saving, setSaving] = useState(false);
  const [createMaterialOpen, setCreateMaterialOpen] = useState(false);
  const [createMaterialType, setCreateMaterialType] = useState("fabric");
  const [createMaterialLineId, setCreateMaterialLineId] = useState(null);

  // Reset each time it re-opens
  useEffect(() => {
    if (open) {
      setSupplier(supplierId || suggestedSupplierId);
      setPurchaseDate(today);
      setWarehouseEntryDate("");
      setInvoiceNo("");
      setDeliveryNoteNo("");
      setLotNo("");
      setSupplierLotNo("");
      setWarehouseLocation("");
      setNotes("");
      setLines(initialLines?.length ? initialLines.map((l) => ({ ...makeEmptyLine(), ...l })) : [makeEmptyLine(initialMaterial)]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, supplierId, suggestedSupplierId, initialMaterial?.id, initialMaterial?.type, initialLines]);

  // Load reference lists
  useEffect(() => {
    if (!open) return;
    Promise.all([
      api.get("/inventory/fabrics").catch(() => ({ data: [] })),
      api.get("/inventory/accessories").catch(() => ({ data: [] })),
      supplierId ? Promise.resolve({ data: [] }) : api.get("/suppliers").catch(() => ({ data: [] })),
      api.get("/warehouses").catch(() => ({ data: [] })),
    ]).then(([f, a, s, w]) => {
      setFabrics(f.data || []);
      setAccessories(a.data || []);
      setSuppliers(s.data || []);
      // Dedupe warehouses by name — SelectItem uses name as value; two warehouses
      // sharing the same display name would produce duplicate Radix keys/values.
      const seenNames = new Set();
      const uniqueWh = (w.data || []).filter((x) => {
        if (x.is_active === false) return false;
        const key = (x.name || "").trim();
        if (!key || seenNames.has(key)) return false;
        seenNames.add(key);
        return true;
      });
      setWarehouses(uniqueWh);
    });
  }, [open, supplierId]);

  const supplierNameResolved = useMemo(() => {
    if (supplierName) return supplierName;
    const s = suppliers.find((x) => x.id === supplier);
    return s?.name || "";
  }, [supplier, suppliers, supplierName]);

  // Update a single line
  const updateLine = (id, patch) =>
    setLines((prev) => prev.map((ln) => (ln.id === id ? { ...ln, ...patch } : ln)));

  const removeLine = (id) =>
    setLines((prev) => (prev.length <= 1 ? prev : prev.filter((ln) => ln.id !== id)));

  const addLine = () => setLines((prev) => [...prev, makeEmptyLine()]);

  // Material picked in a line — auto-fill unit from material default
  const pickMaterial = (lineId, materialType, materialId) => {
    const list = materialType === "fabric" ? fabrics : accessories;
    const m = list.find((x) => x.id === materialId);
    updateLine(lineId, {
      material_id: materialId,
      unit: m?.unit || (materialType === "fabric" ? "kg" : "adet"),
    });
  };

  // Quick-create material dialog handling
  const openQuickCreate = (lineId, materialType) => {
    setCreateMaterialLineId(lineId);
    setCreateMaterialType(materialType);
    setCreateMaterialOpen(true);
  };

  const handleMaterialCreated = (created) => {
    setCreateMaterialOpen(false);
    if (createMaterialType === "fabric") {
      setFabrics((prev) => [...prev, created].sort((a, b) => (a.code || "").localeCompare(b.code || "")));
    } else {
      setAccessories((prev) => [...prev, created].sort((a, b) => (a.code || "").localeCompare(b.code || "")));
    }
    // Auto-select in the originating line
    setTimeout(() => {
      if (createMaterialLineId) {
        updateLine(createMaterialLineId, {
          material_id: created.id,
          unit: created.unit || (createMaterialType === "fabric" ? "kg" : "adet"),
        });
      }
    }, 60);
    toast.success(`${created.code} — ${created.name} eklendi ve seçildi`);
  };

  // Line totals — para birimi satır bazlı; genel toplam para birimi başına gruplanır
  const lineTotal = (ln) => (Number(ln.quantity) || 0) * (Number(ln.unit_price) || 0);
  const totalsByCur = lines.reduce((acc, ln) => {
    const cur = ln.currency || "TRY";
    acc[cur] = (acc[cur] || 0) + lineTotal(ln);
    return acc;
  }, {});
  const grandTotalText = Object.entries(totalsByCur)
    .filter(([, v]) => v > 0)
    .map(([c, v]) => `${CUR_SYMBOL[c] || c}${v.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`)
    .join("  +  ");

  const validLines = lines.filter(
    (ln) => ln.material_id && Number(ln.quantity) > 0 && !(ln.material_type === "product" && ln.has_variants && !ln.variant_id)
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!supplier) { toast.error("Tedarikçi seçin"); return; }
    if (validLines.length === 0) {
      toast.error("En az bir malzeme ve pozitif miktar girin");
      return;
    }
    setSaving(true);
    let successCount = 0;
    let failCount = 0;
    let prodOk = 0;
    try {
      const prodLines = validLines.filter((ln) => ln.material_type === "product");
      if (prodLines.length) {
        try {
          const wh = warehouses.find((w) => w.name === warehouseLocation);
          const { data } = await api.post("/purchase-orders/bulk-create", {
            target_warehouse_id: wh?.id || "", order_notes: notes,
            lines: prodLines.map((ln) => ({
              supplier_id: supplier, kind: "product", product_id: ln.material_id, product_name: ln.product_name || "",
              variant_id: ln.variant_id || "", variant_sku: ln.variant_sku || "", quantity: Number(ln.quantity),
              unit: "adet", unit_price: Number(ln.unit_price) || 0, currency: ln.currency || "TRY",
            })),
          });
          prodOk += prodLines.length;
          toast.success(`Satın alma siparişi oluşturuldu (${(data.created || []).map((p) => p.po_no).join(", ")}) — stok mal kabulde artar`);
        } catch (err) {
          failCount += prodLines.length;
          toast.error(`Ürün satırları: ${formatApiError(err)}`);
        }
      }
      for (const ln of validLines.filter((l) => l.material_type !== "product")) {
        try {
          const payload = {
            supplier_id: supplier,
            material_type: ln.material_type,
            material_id: ln.material_id,
            quantity: Number(ln.quantity),
            unit: ln.unit,
            unit_price: Number(ln.unit_price) || 0,
            currency: ln.currency || "TRY",
            invoice_no: invoiceNo,
            delivery_note_no: deliveryNoteNo,
            lot_no: lotNo,
            supplier_lot_no: supplierLotNo,
            purchase_date: new Date(purchaseDate).toISOString(),
            warehouse_entry_date: warehouseEntryDate
              ? new Date(warehouseEntryDate).toISOString()
              : null,
            warehouse_location: warehouseLocation,
            notes: notes,
          };
          await api.post("/purchases", payload);
          successCount += 1;
        } catch (err) {
          failCount += 1;
          const list = ln.material_type === "fabric" ? fabrics : accessories;
          const m = list.find((x) => x.id === ln.material_id);
          toast.error(`${m?.name || "Satır"}: ${formatApiError(err)}`);
        }
      }

      if (successCount > 0) {
        toast.success(
          failCount === 0
            ? `${successCount} satır başarıyla kaydedildi ve stoğa alındı`
            : `${successCount} satır kaydedildi, ${failCount} satır başarısız`
        );
      }
      if (successCount + prodOk > 0) onSuccess?.();
      if (failCount === 0) {
        onOpenChange?.(false);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm max-w-5xl">
        <DialogHeader>
          <DialogTitle className="text-slate-50 tracking-tight flex items-center gap-2">
            <Truck className="w-5 h-5 text-blue-500" /> Yeni Satın Alma (Çoklu Satır)
          </DialogTitle>
          <DialogDescription className="text-slate-400 text-xs">
            {supplierNameResolved ? (
              <>Tedarikçi: <span className="text-blue-400 font-mono">{supplierNameResolved}</span> · Tek ekranda birden fazla kumaş/aksesuar ekleyebilirsiniz.</>
            ) : (
              <>Tek ekranda birden fazla kumaş/aksesuar ekleyebilirsiniz.</>
            )}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 max-h-[78vh] overflow-y-auto pr-1">
          {/* Common header fields */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            {!supplierId ? (
              <div className="space-y-2 md:col-span-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Tedarikçi *</Label>
                <SearchPicker
                  items={suppliers}
                  value={supplier}
                  onChange={setSupplier}
                  getLabel={(s) => s.name}
                  getSubtitle={(s) => [s.tax_no, s.phone].filter(Boolean).join(" · ")}
                  placeholder="Ünvan, vergi no veya telefon ile arayın…"
                  minChars={1}
                  maxSuggestions={10}
                  testId="new-purchase-supplier"
                />
              </div>
            ) : (
              <div className="md:col-span-2 flex items-center justify-between rounded-sm border border-blue-900/60 bg-blue-950/20 px-3 py-2 text-xs">
                <span className="text-slate-400 font-mono uppercase tracking-widest">Tedarikçi</span>
                <span className="text-blue-300 font-mono font-semibold" data-testid="new-purchase-supplier-locked">{supplierName}</span>
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Alım Tarihi *</Label>
              <Input
                type="date" required value={purchaseDate}
                onChange={(e) => setPurchaseDate(e.target.value)}
                className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500 font-mono"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Depo Giriş Tarihi</Label>
              <Input
                type="date" value={warehouseEntryDate}
                onChange={(e) => setWarehouseEntryDate(e.target.value)}
                className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500 font-mono"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Fatura No</Label>
              <Input value={invoiceNo} onChange={(e) => setInvoiceNo(e.target.value)}
                className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500 font-mono" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">İrsaliye No</Label>
              <Input value={deliveryNoteNo} onChange={(e) => setDeliveryNoteNo(e.target.value)}
                className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500 font-mono" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Lot / Parti No</Label>
              <Input
                data-testid="new-purchase-lot-input"
                value={lotNo}
                onChange={(e) => setLotNo(e.target.value)}
                placeholder="Boş bırakılırsa satın alma sipariş no kullanılır"
                className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500 font-mono"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Üretici Lot No</Label>
              <Input
                data-testid="new-purchase-supplier-lot-input"
                value={supplierLotNo}
                onChange={(e) => setSupplierLotNo(e.target.value)}
                placeholder="Tedarikçinin verdiği parti no"
                className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500 font-mono"
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Depo Konumu</Label>
              <Select
                value={warehouseLocation || "__none"}
                onValueChange={(v) => setWarehouseLocation(v === "__none" ? "" : v)}
              >
                <SelectTrigger data-testid="new-purchase-warehouse-select" className="bg-slate-950 border-slate-700 rounded-sm">
                  <SelectValue placeholder="Depo seçin" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm">
                  <SelectItem value="__none">— Depo seçilmedi —</SelectItem>
                  {warehouses.map((w) => (
                    <SelectItem key={w.id} value={w.name}>
                      <span className="font-mono text-blue-400">{w.code}</span>
                      <span className="ml-2">{w.name}</span>
                    </SelectItem>
                  ))}
                  {warehouses.length === 0 && (
                    <div className="px-2 py-3 text-center text-xs text-slate-500">
                      Depo tanımlı değil. Ayarlar → Depolar'dan ekleyin.
                    </div>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 md:col-span-4">
              <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Notlar</Label>
              <Textarea
                rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
                className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500 resize-none"
              />
            </div>
          </div>

          {/* Lines section */}
          <div className="border-t border-slate-800 pt-4">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs uppercase tracking-widest font-mono text-slate-400">
                Alım Satırları ({lines.length})
              </div>
              <Button
                type="button"
                data-testid="add-purchase-line-btn"
                onClick={addLine}
                variant="outline"
                size="sm"
                className="border-blue-800 bg-blue-950/40 text-blue-300 hover:bg-blue-900/60 rounded-sm h-8 text-xs"
              >
                <Plus className="w-3.5 h-3.5 mr-1" /> Satır Ekle
              </Button>
            </div>

            <div className="space-y-3">
              {lines.map((ln, idx) => (
                <PurchaseLineRow
                  key={ln.id}
                  index={idx}
                  line={ln}
                  fabrics={fabrics}
                  accessories={accessories}
                  supplierId={supplier}
                  onChange={(patch) => updateLine(ln.id, patch)}
                  onPickMaterial={(mt, mid) => pickMaterial(ln.id, mt, mid)}
                  onRemove={() => removeLine(ln.id)}
                  onQuickCreate={(mt) => openQuickCreate(ln.id, mt)}
                  canRemove={lines.length > 1}
                />
              ))}
            </div>
          </div>

          {grandTotalText && (
            <div className="rounded-sm border border-slate-800 bg-slate-950/60 px-3 py-2 text-xs font-mono flex items-center justify-between">
              <span className="text-slate-500 uppercase tracking-widest">
                Genel Toplam ({validLines.length} satır)
              </span>
              <span className="text-blue-400 font-semibold tabular-nums text-base">
                {grandTotalText}
              </span>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange?.(false)}
              className="border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800 rounded-sm">
              İptal
            </Button>
            <Button
              data-testid="save-new-purchase-btn"
              type="submit"
              disabled={(saving || validLines.length === 0) || !(can("purchases", "add") && can("warehouse", "edit"))} title={!(can("purchases", "add") && can("warehouse", "edit")) ? "Alış oluşturma yetkiniz yok (purchases.add + warehouse.edit)" : undefined}
              className="bg-blue-600 hover:bg-blue-500 rounded-sm uppercase tracking-wider text-xs font-semibold transition-colors"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : `${validLines.every((l) => l.material_type === "product") ? "Satın Alma Siparişi Oluştur" : "Kaydet & Stoğa Al"} (${validLines.length})`}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>

      <QuickCreateMaterialDialog
        open={createMaterialOpen}
        onOpenChange={setCreateMaterialOpen}
        materialType={createMaterialType}
        supplierId={supplier}
        supplierName={supplierNameResolved}
        onCreated={handleMaterialCreated}
      />
    </Dialog>
  );
}

/* -------------------------------------------------------------------------- */
/* Purchase line row                                                           */
/* -------------------------------------------------------------------------- */
function PurchaseLineRow({
  index, line, fabrics, accessories, supplierId,
  onChange, onPickMaterial, onRemove, onQuickCreate, canRemove,
}) {
  const isFabric = line.material_type === "fabric";
  const list = isFabric ? fabrics : accessories;

  // Prefer this supplier's materials at the top of the picker
  const orderedMaterials = useMemo(() => {
    if (!supplierId) return list;
    const linked = list.filter((m) => m.primary_supplier_id === supplierId);
    const rest = list.filter((m) => m.primary_supplier_id !== supplierId);
    return [...linked, ...rest];
  }, [list, supplierId]);

  const lineTotal = (Number(line.quantity) || 0) * (Number(line.unit_price) || 0);

  return (
    <div
      data-testid={`purchase-line-${index}`}
      className="rounded-sm border border-slate-800 bg-slate-950/40 p-3"
    >
      <div className="grid grid-cols-1 md:grid-cols-[110px_1fr_120px_100px_130px_130px_32px] gap-2 items-start">
        {/* Type */}
        <div className="space-y-1">
          <Label className="text-[9px] uppercase tracking-widest text-slate-500 font-mono">Tip</Label>
          <Select
            value={line.material_type}
            onValueChange={(v) => onChange({
              material_type: v,
              material_id: "",
              unit: v === "fabric" ? "kg" : "adet",
              variant_id: "", variant_sku: "", product_name: "",
            })}
          >
            <SelectTrigger
              data-testid={`purchase-line-${index}-type`}
              className="bg-slate-950 border-slate-700 rounded-sm h-9 text-xs"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm">
              <SelectItem value="product">Ürün</SelectItem>
              <SelectItem value="fabric">Kumaş</SelectItem>
              <SelectItem value="accessory">Aksesuar</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {line.material_type === "product" ? (
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <Label className="text-[9px] uppercase tracking-widest text-slate-500 font-mono">Ürün + Beden</Label>
              <button type="button" data-testid={`purchase-line-${index}-new-product`}
                onClick={() => window.open("/urunler?new=1", "_blank")}
                title="Mevcut Ürünler sayfasında yeni ürün kartı açar; kaydettikten sonra burada aranabilir"
                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-mono text-emerald-400 hover:text-emerald-300 hover:bg-emerald-950/40 px-1.5 py-0.5 rounded-sm transition-colors">
                <Plus className="w-3 h-3" strokeWidth={3} /> Yeni Ürün
              </button>
            </div>
            <PurchaseProductPicker index={index} line={line} onChange={onChange} />
          </div>
        ) : (
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <Label className="text-[9px] uppercase tracking-widest text-slate-500 font-mono">
              Malzeme (harf başına ara)
            </Label>
            <button
              type="button"
              data-testid={`purchase-line-${index}-quick-create`}
              onClick={() => onQuickCreate(line.material_type)}
              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-mono text-emerald-400 hover:text-emerald-300 hover:bg-emerald-950/40 px-1.5 py-0.5 rounded-sm transition-colors"
              title={`Sistemde yoksa yeni ${isFabric ? "kumaş" : "aksesuar"} ekle`}
            >
              <Plus className="w-3 h-3" strokeWidth={3} />
              Yeni {isFabric ? "Kumaş" : "Aksesuar"}
            </button>
          </div>
          <SearchPicker
            testId={`purchase-line-${index}-material`}
            items={orderedMaterials}
            value={line.material_id}
            onChange={(id) => onPickMaterial(line.material_type, id)}
            getId={(m) => m.id}
            getLabel={(m) => `${m.name || ""} - ${m.code || ""}`}
            getSubtitle={(m) =>
              isFabric
                ? [m.fabric_type, m.color, m.composition].filter(Boolean).join(" · ")
                : [m.category, m.unit].filter(Boolean).join(" · ")
            }
            placeholder={`En az 1 harf yazın… (${isFabric ? "kumaş" : "aksesuar"} adı veya kodu)`}
            minChars={1}
            maxSuggestions={12}
          />
        </div>
        )}

        {/* Quantity */}
        <div className="space-y-1">
          <Label className="text-[9px] uppercase tracking-widest text-slate-500 font-mono">Miktar *</Label>
          <Input
            data-testid={`purchase-line-${index}-qty`}
            type="number" step="0.01" min="0"
            value={line.quantity}
            onChange={(e) => onChange({ quantity: e.target.value })}
            className="bg-slate-950 border-slate-700 rounded-sm h-9 focus-visible:ring-blue-500 font-mono text-xs"
          />
        </div>

        {/* Unit */}
        <div className="space-y-1">
          <Label className="text-[9px] uppercase tracking-widest text-slate-500 font-mono">Birim</Label>
          <Select value={line.unit} onValueChange={(v) => onChange({ unit: v })}>
            <SelectTrigger
              data-testid={`purchase-line-${index}-unit`}
              className="bg-slate-950 border-slate-700 rounded-sm h-9 text-xs"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm">
              <SelectItem value="kg">kg</SelectItem>
              <SelectItem value="metre">metre</SelectItem>
              <SelectItem value="adet">adet</SelectItem>
              <SelectItem value="kon">kon</SelectItem>
              <SelectItem value="paket">paket</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Unit price + currency (satır bazlı) */}
        <div className="space-y-1">
          <Label className="text-[9px] uppercase tracking-widest text-slate-500 font-mono">Birim Fiyat / Para Birimi</Label>
          <div className="flex gap-1.5">
            <Input
              data-testid={`purchase-line-${index}-price`}
              type="number" step="0.01" min="0"
              value={line.unit_price}
              onChange={(e) => onChange({ unit_price: e.target.value })}
              className="bg-slate-950 border-slate-700 rounded-sm h-9 focus-visible:ring-blue-500 font-mono text-xs flex-1 min-w-0"
            />
            <Select value={line.currency || "TRY"} onValueChange={(v) => onChange({ currency: v })}>
              <SelectTrigger
                data-testid={`purchase-line-${index}-currency`}
                className="bg-slate-950 border-slate-700 rounded-sm h-9 text-xs w-[76px] shrink-0"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm">
                {CUR_LIST.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Line total */}
        <div className="space-y-1">
          <Label className="text-[9px] uppercase tracking-widest text-slate-500 font-mono">Satır Toplam</Label>
          <div
            data-testid={`purchase-line-${index}-total`}
            className="h-9 flex items-center px-2 rounded-sm border border-slate-800 bg-slate-900/60 font-mono text-xs text-blue-300 tabular-nums"
          >
            {CUR_SYMBOL[line.currency || "TRY"]}{lineTotal.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
        </div>

        {/* Remove */}
        <div className="space-y-1">
          <Label className="text-[9px] uppercase tracking-widest text-slate-500 font-mono">&nbsp;</Label>
          <button
            type="button"
            data-testid={`purchase-line-${index}-remove`}
            onClick={onRemove}
            disabled={!canRemove}
            className="h-9 w-9 flex items-center justify-center rounded-sm border border-slate-800 text-slate-500 hover:text-red-400 hover:bg-red-950/30 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            title="Satırı sil"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Quick create material dialog (unchanged behaviour, moved from previous file) */
/* -------------------------------------------------------------------------- */
function QuickCreateMaterialDialog({ open, onOpenChange, materialType, supplierId, supplierName, onCreated }) {
  const isFabric = materialType === "fabric";
  const emptyFabric = {
    code: "", name: "", fabric_type: "", color: "", composition: "", weight_gsm: "",
    stock_kg: 0, stock_metre: 0, min_stock_kg: "", location: "",
  };
  const emptyAcc = {
    code: "", name: "", category: "", unit: "adet",
    stock: 0, min_stock: "", location: "",
  };
  const [form, setForm] = useState(isFabric ? emptyFabric : emptyAcc);
  const [saving, setSaving] = useState(false);
  const [warehouses, setWarehouses] = useState([]);
  const [defs, setDefs] = useState([]);

  useEffect(() => {
    if (open) {
      setForm(isFabric ? emptyFabric : emptyAcc);
      const kind = isFabric ? "fabric_type" : "accessory_category";
      Promise.all([
        api.get("/warehouses").catch(() => ({ data: [] })),
        api.get(`/definitions/${kind}`).catch(() => ({ data: [] })),
      ]).then(([w, d]) => {
        const seen = new Set();
        const uniqueWh = (w.data || []).filter((x) => {
          if (x.is_active === false) return false;
          const key = (x.name || "").trim();
          if (!key || seen.has(key)) return false;
          seen.add(key);
          return true;
        });
        setWarehouses(uniqueWh);
        setDefs((d.data || []).filter((x) => x.is_active !== false));
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isFabric]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.code.trim() || !form.name.trim()) {
      toast.error("Kod ve isim zorunlu");
      return;
    }
    setSaving(true);
    try {
      const payload = isFabric
        ? {
            code: form.code.trim(),
            name: form.name.trim(),
            fabric_type: form.fabric_type || "",
            color: form.color || "",
            composition: form.composition || "",
            weight_gsm: Number(form.weight_gsm) || 0,
            stock_kg: 0,
            stock_metre: 0,
            min_stock_kg: Number(form.min_stock_kg) || 0,
            location: form.location || "",
            primary_supplier_id: supplierId || "",
          }
        : {
            code: form.code.trim(),
            name: form.name.trim(),
            category: form.category || "genel",
            unit: form.unit || "adet",
            stock: 0,
            min_stock: Number(form.min_stock) || 0,
            location: form.location || "",
            primary_supplier_id: supplierId || "",
          };
      const endpoint = isFabric ? "/inventory/fabrics" : "/inventory/accessories";
      const { data } = await api.post(endpoint, payload);
      onCreated?.(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm max-w-md">
        <DialogHeader>
          <DialogTitle className="text-slate-50 tracking-tight flex items-center gap-2">
            <Plus className="w-4 h-4 text-emerald-500" />
            Yeni {isFabric ? "Kumaş" : "Aksesuar"} Ekle
          </DialogTitle>
          {supplierName && (
            <DialogDescription className="text-slate-400 text-xs">
              Tedarikçi: <span className="text-blue-400 font-mono">{supplierName}</span> otomatik bağlanacak.
            </DialogDescription>
          )}
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Kod *</Label>
              <Input
                data-testid="qc-material-code"
                required
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                placeholder={isFabric ? "KMS-020" : "AKS-030"}
                className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-emerald-500 font-mono"
              />
            </div>
            {isFabric ? (
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Kumaş Türü</Label>
                <Select value={form.fabric_type || "__none"} onValueChange={(v) => setForm({ ...form, fabric_type: v === "__none" ? "" : v })}>
                  <SelectTrigger data-testid="qc-fabric-type-select" className="bg-slate-950 border-slate-700 rounded-sm">
                    <SelectValue placeholder="Kumaş türü seçin" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm max-h-72">
                    <SelectItem value="__none">— Belirtilmedi —</SelectItem>
                    {defs.map((d) => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}
                    {defs.length === 0 && (
                      <div className="px-2 py-3 text-center text-xs text-slate-500">Tanımlı tür yok. Ayarlar → Tanımlar'dan ekleyin.</div>
                    )}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Kategori</Label>
                <Select value={form.category || "__none"} onValueChange={(v) => setForm({ ...form, category: v === "__none" ? "" : v })}>
                  <SelectTrigger data-testid="qc-accessory-category-select" className="bg-slate-950 border-slate-700 rounded-sm">
                    <SelectValue placeholder="Kategori seçin" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm max-h-72">
                    <SelectItem value="__none">— Belirtilmedi —</SelectItem>
                    {defs.map((d) => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}
                    {defs.length === 0 && (
                      <div className="px-2 py-3 text-center text-xs text-slate-500">Tanımlı kategori yok. Ayarlar → Tanımlar'dan ekleyin.</div>
                    )}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2 col-span-2">
              <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">İsim *</Label>
              <Input
                data-testid="qc-material-name"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder={isFabric ? "Ripstop %65 Polyester %35 Pamuk" : "YKK Metal Fermuar 70cm"}
                className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-emerald-500"
              />
            </div>
            {isFabric ? (
              <>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Renk</Label>
                  <Input
                    value={form.color || ""}
                    onChange={(e) => setForm({ ...form, color: e.target.value })}
                    placeholder="Lacivert"
                    className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-emerald-500"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Kompozisyon</Label>
                  <Input
                    value={form.composition || ""}
                    onChange={(e) => setForm({ ...form, composition: e.target.value })}
                    placeholder="%65 PES %35 CO"
                    className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-emerald-500"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Gramaj (g/m²)</Label>
                  <Input
                    type="number"
                    step="1"
                    min="0"
                    value={form.weight_gsm || ""}
                    onChange={(e) => setForm({ ...form, weight_gsm: e.target.value })}
                    className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-emerald-500 font-mono"
                  />
                </div>
              </>
            ) : (
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Birim</Label>
                <Select value={form.unit} onValueChange={(v) => setForm({ ...form, unit: v })}>
                  <SelectTrigger className="bg-slate-950 border-slate-700 rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm">
                    <SelectItem value="adet">adet</SelectItem>
                    <SelectItem value="metre">metre</SelectItem>
                    <SelectItem value="kg">kg</SelectItem>
                    <SelectItem value="kon">kon</SelectItem>
                    <SelectItem value="paket">paket</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Depo Konumu</Label>
              <Select
                value={form.location || "__none"}
                onValueChange={(v) => setForm({ ...form, location: v === "__none" ? "" : v })}
              >
                <SelectTrigger data-testid="qc-material-location" className="bg-slate-950 border-slate-700 rounded-sm">
                  <SelectValue placeholder="Depo seçin" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm">
                  <SelectItem value="__none">— Depo seçilmedi —</SelectItem>
                  {warehouses.map((w) => (
                    <SelectItem key={w.id} value={w.name}>
                      <span className="font-mono text-blue-400">{w.code}</span>
                      <span className="ml-2">{w.name}</span>
                    </SelectItem>
                  ))}
                  {warehouses.length === 0 && (
                    <div className="px-2 py-3 text-center text-xs text-slate-500">Depo tanımlı değil.</div>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">
                {isFabric ? "Min Stok (kg)" : "Min Stok"}
              </Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={isFabric ? (form.min_stock_kg || "") : (form.min_stock || "")}
                onChange={(e) => setForm(isFabric
                  ? { ...form, min_stock_kg: e.target.value }
                  : { ...form, min_stock: e.target.value })}
                className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-emerald-500 font-mono"
              />
            </div>
          </div>
          <div className="text-[10px] text-slate-500 font-mono border border-slate-800 bg-slate-950/60 rounded-sm px-2 py-1.5">
            Not: Stok bu formda 0 olarak açılır. Ana miktar Yeni Satın Alma ekranında girildiğinde otomatik stoğa alınır.
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange?.(false)} className="border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800 rounded-sm">
              İptal
            </Button>
            <Button
              data-testid="save-qc-material-btn"
              type="submit"
              disabled={saving}
              className="bg-emerald-600 hover:bg-emerald-500 rounded-sm uppercase tracking-wider text-xs font-semibold transition-colors"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Ekle & Seç"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
